import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import {
  Business,
  PersonAdd,
  Schedule,
  Cancel,
} from '@mui/icons-material';
import { organizationApi } from '../services/organizationApi';
import { 
  OrganizationInvitation, 
  InvitationStatus,
  getRoleLabel,
  getRoleColor,
} from '../services/organizationApi';

interface PendingInvitationsProps {
  invitations?: OrganizationInvitation[];
  onInvitationAccepted?: () => void;
  onInvitationDeclined?: () => void;
  useExternalData?: boolean;
}

const PendingInvitations: React.FC<PendingInvitationsProps> = ({
  invitations: externalInvitations,
  onInvitationAccepted,
  onInvitationDeclined,
  useExternalData = false,
}) => {
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (useExternalData && externalInvitations) {
      setInvitations(externalInvitations.filter(inv => {
        const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
        return statusValue === InvitationStatus.PENDING || statusValue === 0;
      }));
      setLoading(false);
    } else {
      loadInvitations();
    }
  }, [useExternalData, externalInvitations]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationApi.getMyInvitations();
      setInvitations(data.filter(inv => {
        const statusValue = typeof inv.status === "string" ? parseInt(inv.status) : inv.status;
        return statusValue === InvitationStatus.PENDING || statusValue === 0;
      }));
    } catch (err: any) {
      console.error('Failed to load invitations:', err);
      setError('Failed to load pending invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitation: OrganizationInvitation) => {
    try {
      setProcessing(invitation.id);
      setError(null);
      
      if (useExternalData && onInvitationAccepted) {
        // Use external handler if provided
        onInvitationAccepted();
      } else {
        // Use internal API call
        await organizationApi.acceptInvitation(invitation.id);
        // Remove from list and notify parent
        setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
        onInvitationAccepted?.();
      }
      
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeclineInvitation = async (invitation: OrganizationInvitation) => {
    try {
      setProcessing(invitation.id);
      setError(null);
      
      if (useExternalData && onInvitationDeclined) {
        // Use external handler if provided
        onInvitationDeclined();
      } else {
        // Use internal API call
        await organizationApi.declineInvitation(invitation.id);
        // Remove from list and notify parent
        setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
        onInvitationDeclined?.();
      }
      
    } catch (err: any) {
      console.error('Failed to decline invitation:', err);
      setError('Failed to decline invitation. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryColor = (expiryDate: string) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days <= 1) return 'error';
    if (days <= 3) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (invitations.length === 0) {
    return (
      <Box textAlign="center" p={3}>
        <Business sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Pending Invitations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You don't have any pending organization invitations at the moment.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Pending Invitations ({invitations.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {invitations.map((invitation) => {
          const daysUntilExpiry = getDaysUntilExpiry(invitation.expires_at);
          const expiryColor = getExpiryColor(invitation.expires_at);
          
          return (
            <Grid 
              key={invitation.id}
              sx={{
                width: { xs: '100%', md: '50%' },
                minWidth: { xs: '100%', md: '50%' }
              }}
            >
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {invitation.organization?.name || 'Unknown Organization'}
                      </Typography>
                      {invitation.organization?.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {invitation.organization.description}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={`Role: ${getRoleLabel(invitation.role)}`}
                        sx={{
                          backgroundColor: getRoleColor(invitation.role),
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <Schedule sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expires {formatDate(invitation.expires_at)}
                    </Typography>
                    <Chip
                      label={`${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''} left`}
                      color={expiryColor}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PersonAdd />}
                      onClick={() => handleAcceptInvitation(invitation)}
                      disabled={processing === invitation.id}
                      fullWidth
                    >
                      {processing === invitation.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Accept'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => handleDeclineInvitation(invitation)}
                      disabled={processing === invitation.id}
                      fullWidth
                    >
                      {processing === invitation.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Decline'
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PendingInvitations;
